import { Box, Flex, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  FormFeedback,
  Input,
  Modal,
  useToast
} from '@morressier/alembic';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FieldLabel } from '../Field';
import { maxPasswordLimit } from '../UserRegistrationSteps/StepThree/signupHook';
import { faLock } from '@fortawesome/pro-light-svg-icons';
import { useChangePasswordMutation } from 'src/api/signUpMutation';
import { useKeycloak } from '../AuthHOC';
import { passwordRegex } from '../FormField/common/CustomFormFIeldPasswordInput';
import PasswordVisibilityToggle from '../FormField/common/PasswordVisibilityToggle';

type ConfirmPasswordForm = {
  newPassword: string;
  confirmNewPassword: string;
};

const ChangePassword = () => {
  const toast = useToast();
  const { logout } = useKeycloak();

  const changePasswordMutation = useChangePasswordMutation();

  const [isOpen, setIsOpen] = useState(false);
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);

  const methods = useForm<ConfirmPasswordForm>();
  const {
    register,
    formState: { errors },
    trigger,
    getValues,
    handleSubmit,
    reset,
    clearErrors,
    watch
  } = methods;

  const password = watch('newPassword');
  const confirmPassword = watch('confirmNewPassword');

  const showToast = (
    type: 'success' | 'error',
    title: string,
    description: string
  ) => {
    toast({ variant: type, title, description, isClosable: true });
  };

  const onClose = () => {
    reset();
    clearErrors();
    setIsNewPasswordVisible(false);
    setIsConfirmPasswordVisible(false);
    setIsOpen(false);
  };

  const onSubmit = async () => {
    try {
      const response = await changePasswordMutation.mutateAsync({
        password: getValues('newPassword')
      });

      const message =
        response?.message?.details?.[0]?.message ||
        response?.message?.message ||
        response?.message ||
        'Internal Server Error';

      if (!response.isSuccess) {
        showToast('error', 'Error', message);
        return;
      }

      showToast(
        'success',
        'Your password has been changed successfully. For your security, please log in again with your new password to continue using your account',
        message
      );
      logout();
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Something went wrong');
    }
  };

  useEffect(() => {
    if (password) {
      trigger('newPassword');
    }
    if (password && confirmPassword) {
      trigger('confirmNewPassword');
    }
  }, [password, confirmPassword, trigger]);

  return (
    <>
      <Button variant="secondary" onClick={() => setIsOpen(true)}>
        <Box>
          <FontAwesomeIcon icon={faLock} />
        </Box>
        <Text> Change Passwords</Text>
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={() => {}}
        width="700px"
        closeButtonProps={{
          visibility: 'hidden'
        }}
      >
        <FormProvider {...methods}>
          <Flex direction="column" px={8} pt={8}>
            {/* Header */}
            <Flex direction="column" gap={3}>
              <Box>
                <FontAwesomeIcon icon={faLock} size="2xl" />
              </Box>
              <Text textStyle="headingTitle">Change Password</Text>
              <Text>
                Your password must be at least 8 characters and include at least
                one uppercase letter, one lowercase letter and one number.
              </Text>
            </Flex>

            {/* Form */}
            <form
              autoComplete="on"
              name="change-password"
              onSubmit={handleSubmit(onSubmit)}
            >
              <Flex direction="column" my={5} gap={3}>
                {/* New Password */}
                <Flex direction="column" gap={3} flex={1}>
                  <FieldLabel label="New Password" isRequired subText="" />
                  <Input
                    id="newPassword"
                    type={isNewPasswordVisible ? 'text' : 'password'}
                    iconRight={
                      <PasswordVisibilityToggle
                        isVisible={isNewPasswordVisible}
                        onToggle={() =>
                          setIsNewPasswordVisible(current => !current)
                        }
                        ariaLabel="Toggle visibility for new password"
                      />
                    }
                    placeholder="Set a new Password"
                    {...register('newPassword', {
                      required: 'Enter a new password',
                      maxLength: {
                        value: maxPasswordLimit,
                        message: `New password cannot exceed ${maxPasswordLimit} characters.`
                      },
                      validate: value => {
                        if (value.trim().length === 0) {
                          return `New Password cannot be just whitespace`;
                        }
                        // 2. No leading or trailing spaces
                        if (/^\s|\s$/.test(value)) {
                          return 'No leading or trailing spaces allowed';
                        }
                        // Password complexity regex check
                        if (!passwordRegex.test(value)) {
                          return 'Password must be at least 8 characters and include at least one uppercase letter, one lowercase letter and one number';
                        }
                        return true;
                      }
                    })}
                    isInvalid={!!errors.newPassword}
                    autoComplete="new-password"
                  />
                  {errors.newPassword && (
                    <FormFeedback variant="error">
                      {errors.newPassword.message}
                    </FormFeedback>
                  )}
                </Flex>

                {/* Confirm Password */}
                <Flex direction="column" gap={3} flex={1}>
                  <FieldLabel
                    label="Confirm New Password"
                    isRequired
                    subText=""
                  />
                  <Input
                    id="confirmNewPassword"
                    type={isConfirmPasswordVisible ? 'text' : 'password'}
                    iconRight={
                      <PasswordVisibilityToggle
                        isVisible={isConfirmPasswordVisible}
                        onToggle={() =>
                          setIsConfirmPasswordVisible(current => !current)
                        }
                        ariaLabel="Toggle visibility for confirm password"
                      />
                    }
                    placeholder="Confirm your new password"
                    {...register('confirmNewPassword', {
                      required: 'Please confirm your password',
                      maxLength: {
                        value: maxPasswordLimit,
                        message: `Confirm new password cannot exceed ${maxPasswordLimit} characters`
                      },
                      validate: value => {
                        if (value.trim().length === 0) {
                          return `Confirm New Password cannot be just whitespace`;
                        }
                        // 2. No leading or trailing spaces
                        if (/^\s|\s$/.test(value)) {
                          return 'No leading or trailing spaces allowed';
                        }
                        // Password complexity regex check
                        if (!passwordRegex.test(value)) {
                          return 'Password must be at least 8 characters and include at least one uppercase letter, one lowercase letter and one number';
                        }
                        // Check if passwords match
                        if (value !== getValues('newPassword')) {
                          return 'This password does not match the new password set above. Please ensure both passwords match';
                        }
                        return true;
                      }
                    })}
                    isInvalid={!!errors.confirmNewPassword}
                    autoComplete="new-password"
                  />

                  {errors.confirmNewPassword && (
                    <FormFeedback variant="error">
                      {errors.confirmNewPassword.message}
                    </FormFeedback>
                  )}
                </Flex>

                {/* Actions */}
                <Flex justifyContent="flex-end" gap={3}>
                  <Button variant="secondary" onClick={onClose} type="button">
                    Exit Without Saving
                  </Button>
                  <Button
                    type="submit"
                    disabled={changePasswordMutation.isLoading}
                  >
                    {changePasswordMutation.isLoading
                      ? 'Saving...'
                      : 'Save Changes'}
                  </Button>
                </Flex>
              </Flex>
            </form>
          </Flex>
        </FormProvider>
      </Modal>
    </>
  );
};

export default ChangePassword;
